import { useEffect, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import styles from './FormGroup.module.css';
import {
  fetchData,
  sendData,
  updateData,
  uploadFile
} from '../../../../_metronic/helpers/utils/api';
import { createKeyValuePairs } from '../../../../_metronic/helpers/utils/utils';
import type { FormProps, FormData } from '../../../../_metronic/helpers/utils/types';
import { useLanguage } from '../../../../_metronic/helpers/utils/context';
import ReactQuill from 'react-quill';

const FormGroup = ({ formId, heading, inputData, pageId, sectionName }: FormProps) => {
  const { language } = useLanguage();
  const initialFormData = createKeyValuePairs(inputData);
  const [formData, setFormData] = useState<FormData[]>([initialFormData]);
  const [selectedInput, setSelectedInput] = useState<string[]>([]);
  const [showSuccessMessage, setShowSuccessMessage] = useState(true);

  const query = useQuery({
    queryKey: [pageId, sectionName, formId],
    queryFn: fetchData({ pageId, sectionName, key: formId }),
    onSuccess: (data) => {
      if (data?.[language] !== undefined && data?.[language] !== null) {
        setFormData(data[language] as FormData[]);
      } else {
        setFormData([initialFormData]);
      }
    }
  });

  useEffect(() => {
    if (query?.data?.[language] !== undefined && query?.data?.[language] !== null) {
      setFormData(query.data[language] as FormData[]);
    } else {
      setFormData([initialFormData]);
    }
  }, [language, query.data]);

  const postMutation = useMutation(sendData);
  const patchMutation = useMutation(updateData);
  const uploadFileMutation = useMutation(uploadFile);

  useEffect(() => {
    if (postMutation.isSuccess || patchMutation.isSuccess) {
      setShowSuccessMessage(true);

      const timer = setTimeout(() => {
        setShowSuccessMessage(false);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [postMutation.isSuccess, patchMutation.isSuccess]);

  const handleOnChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { id, value } = event.target;
    const [inputId, index] = id.split('-');
    setFormData((prevData) => {
      const updatedData = [...prevData];
      updatedData[index] = { ...updatedData[index], [inputId]: value };
      return updatedData;
    });
  };

  const handleOnChange2 = (value: string, id: string) => {
    const event = {
      target: {
        id,
        value
      }
    };
    handleOnChange(event as React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>);
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const imageUpload = async () => {
      const { id } = event.target;
      const [inputId, index] = id.split('-');
      const file = event.target?.files?.[0];

      setSelectedInput((prevData) => [...prevData, id]);

      if (file === undefined) {
        return;
      }

      try {
        const imageUrl: string = await uploadFileMutation.mutateAsync(file);
        setSelectedInput((prevData) => prevData.filter((idToRemove) => idToRemove !== id));

        setFormData((prevData) => {
          const updatedData = [...prevData];
          updatedData[index] = { ...updatedData[index], [inputId]: imageUrl };
          return updatedData;
        });
      } catch (error) {
        console.error('Error uploading image', error);
      }
    };

    void imageUpload();
  };

  const handleAddInputs = () => {
    setFormData((prevData) => [...prevData, initialFormData]);
  };

  const handleDeleteInputs = (index: number) => () => {
    setFormData((prevData) => {
      const updatedData = [...prevData];
      updatedData.splice(index, 1);

      return updatedData;
    });
  };

  const handleSubmit = (event: React.FormEvent) => {
    const submit = async () => {
      event.preventDefault();

      const body = {
        pageId,
        sectionName,
        key: formId,
        [language]: formData
      };

      const { data } = query;
      const id: number | undefined = data?.id;

      if (data !== undefined && id !== undefined) {
        await patchMutation.mutateAsync({ id, body });
      } else {
        await postMutation.mutateAsync(body);
      }

      await query.refetch();
    };

    void submit();
  };

  const getImage = (key: string) => {
    const [id, index] = key.split('-');
    const field = inputData.find((field) => field.id === id);

    if (!field) {
      return null;
    }

    const { backgroundColor } = field;
    const imageStyle = backgroundColor ? { backgroundColor } : {};

    if (!selectedInput.includes(key)) {
      if (formData[index][id] !== '' && formData[index][id] !== undefined) {
        return <img className={styles.image} src={formData[index][id]} alt='' style={imageStyle} />;
      } else {
        return <p className={`warning ${styles.message}`}>No file chosen</p>;
      }
    }

    if (uploadFileMutation.isLoading) {
      return <p className={`loading ${styles.message}`}>Uploading image...</p>;
    }

    if (
      uploadFileMutation.isError ||
      (uploadFileMutation.isSuccess && uploadFileMutation.data === undefined)
    ) {
      return <p className={`error ${styles.message}`}>Error uploading image</p>;
    }
  };

  const getInput = (field: typeof inputData[number], index: number) => {
    const { id, label, type, info, options } = field;
    const key = `${id}-${index}`;

    switch (type) {
      case 'input':
        return (
          <input
            className={styles.input}
            id={key}
            placeholder={label}
            value={formData[index][id] ?? ''}
            onChange={handleOnChange}
          />
        );

      case 'image':
        return (
          <div className={styles.imageInputWrap}>
            <div className={styles.fileInputWrap}>
              <input
                className={styles.fileInput}
                id={key}
                type='file'
                onChange={handleImageUpload}
              />
              <label className={styles.imageLabel} htmlFor={key}>
                Upload Image
              </label>
              <p className={styles.info}>{info}</p>
            </div>
            {getImage(key)}
          </div>
        );

      case 'textarea':
        return (
          <textarea
            className={styles.textarea}
            placeholder={label}
            id={key}
            value={formData[index][id] ?? ''}
            onChange={handleOnChange}
          />
        );

      case 'richTextEditor':
        return (
          <ReactQuill
            className={styles.quillEditor}
            theme='snow'
            value={formData[index][id] ?? ''}
            onChange={(value) => handleOnChange2(value, key)}
          />
        );

      case 'dropdown':
        return (
          <select
            className={styles.select}
            id={key}
            value={formData[index][id] ?? ''}
            onChange={handleOnChange}
          >
            {options?.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        );

      default:
        return null;
    }
  };

  const getInputs = (index: number) => {
    return inputData.map((field) => {
      // If the field has a isShown function and it returns false, don't show the field
      if (field.isShown && !field.isShown(formData[index])) {
        return null;
      }
      return (
        <div className={styles.inputWrap} key={field.id}>
          <label className={styles.label}>{field.label}</label>
          {getInput(field, index)}
        </div>
      );
    });
  };

  const getForm = () => {
    if (formData.length == 0) {
      return (
        <div className={styles.form}>
          <h1 className={styles.title}>{heading}</h1>
        </div>
      );
    }

    return formData.map((_, index) => (
      <div className={styles.form} key={index}>
        <h1 className={styles.title}>{`${heading} ${index + 1}`}</h1>

        <div className={styles.section}>{getInputs(index)}</div>

        <button
          className={`btn btn-danger ${styles.deleteButton}`}
          type='button'
          onClick={handleDeleteInputs(index)}
        >
          Delete this group
        </button>
      </div>
    ));
  };

  const getButtonText = () => {
    return postMutation.isLoading || patchMutation.isLoading ? 'Saving...' : 'Save Changes';
  };

  const getSubmitMessage = () => {
    if (
      postMutation.isError ||
      patchMutation.isError ||
      (postMutation.isSuccess && postMutation.data === undefined) ||
      (patchMutation.isSuccess && patchMutation.data === undefined)
    ) {
      return <p className={`error ${styles.buttonMessage}`}>Error saving changes</p>;
    }

    if (postMutation.isSuccess || patchMutation.isSuccess) {
      return <p className={`success ${styles.buttonMessage}`}>Changes saved successfully</p>;
    }
  };

  const getContent = () => {
    if (query.isLoading) {
      return <p className='loading-screen'>Fetching details...</p>;
    }

    if (query.isError) {
      return <p className='error-screen'>Error fetching details</p>;
    }

    return (
      <>
        <section className={styles.formWrap}>{getForm()}</section>
        <div className={`btn btn-success ${styles.addButton}`} onClick={handleAddInputs}>
          Add another group
        </div>
        <button
          className={`btn btn-primary btn-lg ${styles.submitButton}`}
          disabled={postMutation.isLoading || patchMutation.isLoading}
        >
          {getButtonText()}
        </button>
        {showSuccessMessage && getSubmitMessage()}
      </>
    );
  };

  return (
    <form className={styles.container} onSubmit={handleSubmit}>
      {getContent()}
    </form>
  );
};

export default FormGroup;
